import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Zap, 
  Activity, 
  TrendingUp, 
  Lightbulb, 
  Heart, 
  CheckCircle, 
  Star,
  BarChart3,
  Target,
  Microscope,
  Calendar,
  AlertCircle
} from 'lucide-react';
import HeartLogo from './HeartLogo';

const SynergizeProtocol = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const protocolSteps = [
    {
      step: 1,
      title: "Substrate Loading",
      description: "Specialized supplemental compounds optimize electron transport chain substrate availability",
      icon: Target,
      details: [
        "Targeted mitochondrial cofactor delivery",
        "Enhanced NAD+/NADH ratios",
        "Optimized cellular energy precursors",
        "Bioavailable substrate formulation"
      ]
    },
    {
      step: 2,
      title: "Photobiomodulation Activation",
      description: "Precision light therapy activates loaded substrates for enhanced ATP generation",
      icon: Lightbulb,
      details: [
        "Specific wavelength targeting",
        "Cellular energy pathway activation",
        "Mitochondrial respiratory enhancement",
        "Synergistic compound amplification"
      ]
    },
    {
      step: 3,
      title: "ATP Generation Enhancement",
      description: "Optimized cellular energy production through synergistic substrate-light interaction",
      icon: Zap,
      details: [
        "Increased ATP synthesis efficiency",
        "Enhanced cellular energy availability",
        "Improved metabolic function",
        "Sustained energy production"
      ]
    },
    {
      step: 4,
      title: "Cardiac Function Optimization",
      description: "Improved cardiac diastolic function through enhanced cellular energy availability",
      icon: Heart,
      details: [
        "Enhanced diastolic relaxation",
        "Improved E/A ratio measurements",
        "Optimized cardiac performance",
        "Sustained functional benefits"
      ]
    }
  ];

  const clinicalData = [
    {
      metric: "E/A Ratio Improvement",
      value: "Significant Enhancement",
      description: "Targeted echocardiographic measurements show improved diastolic function",
      icon: BarChart3
    },
    {
      metric: "ATP Generation",
      value: "Enhanced Efficiency",
      description: "Measurable improvement in cellular energy production",
      icon: Zap
    },
    {
      metric: "Cardiac Function",
      value: "Optimized Performance",
      description: "Improved diastolic relaxation and cardiac efficiency",
      icon: Activity
    },
    {
      metric: "Clinical Validation",
      value: "First-in-Man Data",
      description: "Pioneering human studies demonstrate protocol effectiveness",
      icon: Microscope
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors duration-200"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center space-x-4">
                <HeartLogo className="w-12 h-12" animate={true} />
                <span className="text-xl font-semibold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                  Nyquist Therapeutics
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm font-medium">
                Flagship Protocol
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(17, 24, 39, 0.85), rgba(17, 24, 39, 0.9)), url('https://images.unsplash.com/photo-1614577384898-a80d23ad60a0')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-teal-900/20 via-transparent to-teal-900/20 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center space-y-8"
            {...fadeInUp}
          >
            {/* Featured Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="flex justify-center mb-4"
            >
              <HeartLogo className="w-28 h-28 md:w-36 md:h-36" animate={true} />
            </motion.div>
            
            <motion.div
              className="inline-flex items-center space-x-3 bg-gray-800/50 backdrop-blur-sm rounded-full px-6 py-3 border border-teal-500/30"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <Star className="w-5 h-5 text-teal-400" />
              <span className="text-teal-400 font-semibold">Flagship Protocol</span>
            </motion.div>
            
            <h1 className="text-6xl md:text-8xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 bg-clip-text text-transparent">
                SYNERGIZE
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Revolutionary synergistic utilisation of targeted supplemental compounds with precision 
              photobiomodulation to optimize ATP generation and enhance the energy required for cardiac diastolic function
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {['ATP Enhancement', 'Cardiac Optimization', 'First-in-Man Data', 'E/A Ratio Improvement'].map((tag, index) => (
                <motion.div
                  key={index}
                  className="px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full border border-gray-600"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.5 }}
                >
                  {tag}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Protocol Overview */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
                Protocol Mechanism
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              The Synergize protocol leverages the synergy between electron transport chain 
              substrate loading and photobiomodulation activation for unprecedented cardiac optimization.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 xl:grid-cols-4 gap-8">
            {protocolSteps.map((step, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="bg-gray-900/70 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all duration-300 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-teal-500/30">
                      {step.step}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-teal-400 transition-colors duration-300">
                    {step.title}
                  </h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <ul className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-400">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Connection Arrow */}
                {index < protocolSteps.length - 1 && (
                  <div className="hidden xl:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <div className="w-8 h-0.5 bg-gradient-to-r from-teal-500 to-teal-600"></div>
                    <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-4 border-l-teal-600 border-t-2 border-b-2 border-t-transparent border-b-transparent"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinical Data Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
                Clinical Evidence
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              First-in-man data demonstrates significant improvements in cardiac diastolic 
              function through targeted echocardiographic E/A ratio measurements.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {clinicalData.map((data, index) => (
              <motion.div
                key={index}
                className="bg-gray-800/70 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 hover:border-teal-500/50 transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <data.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{data.metric}</h3>
                <p className="text-2xl font-bold text-teal-400 mb-3">{data.value}</p>
                <p className="text-sm text-gray-400">{data.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Echo Study Results */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-white mb-4">
                Echocardiographic Study Results
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Authentic clinical data from our echo study demonstrating the effectiveness 
                of the Synergize protocol on cardiac function parameters.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  url: "https://i.imgur.com/VmaiMBL.jpeg",
                  title: "Cardiac Structure Analysis",
                  description: "Examples of first in man echocardiographic guided cardiac photobiomodulation measuring changes in E/A ratios."
                },
                {
                  url: "https://i.imgur.com/2d9izp4.jpeg",
                  title: "Diastolic Function Assessment",
                  description: "Examples of first in man echocardiographic guided cardiac photobiomodulation measuring changes in E/A ratios."
                },
                {
                  url: "https://i.imgur.com/omq88aW.jpeg",
                  title: "Interventricular Function",
                  description: "Examples of first in man echocardiographic guided cardiac photobiomodulation measuring changes in E/A ratios."
                }
              ].map((study, index) => (
                <motion.div
                  key={index}
                  className="group relative bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700 hover:border-teal-500/50 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={study.url}
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent" />
                    <div className="absolute top-4 right-4">
                      <div className="bg-teal-500/20 text-teal-400 px-3 py-1 rounded-full text-xs font-medium">
                        Clinical Data
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h4 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors duration-300">
                      {study.title}
                    </h4>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      {study.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Technical Specifications Section */}
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 border border-teal-500/30">
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-teal-400 mb-2">
                    THOR Giant Laser Cluster Specifications
                  </h3>
                  <p className="text-gray-300 text-lg">
                    Cutting-Edge Cardiac Photobiomodulation Protocol
                  </p>
                </div>
              </div>
              
              <div className="bg-gray-900/50 rounded-xl p-6">
                <p className="text-gray-300 leading-relaxed mb-6">
                  The photobiomodulation laser device used was the <span className="text-teal-400 font-semibold">THOR Giant Laser Cluster</span>, 
                  a hand-held unit comprising <span className="text-white font-semibold">80 × 810 nm, 200 mW laser diodes</span> (total output: 16 W) 
                  arranged in a 100 cm² active area (14.6 × 6.9 cm). Each laser diode produced a peak irradiance of 2 W/cm² when applied in contact 
                  with the skin over the <span className="text-teal-400 font-semibold">cardiac apex and sternum</span>, located in the 5th intercostal space at the midclavicular line 
                  (corresponding to the mitral auscultation area). Treatment duration was 30 seconds.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-teal-400 font-semibold mb-3">Device Configuration</h4>
                      <ul className="space-y-2 text-gray-300 text-sm">
                        <li>• <span className="text-white">80 laser diodes</span> at 810 nm wavelength</li>
                        <li>• <span className="text-white">200 mW per diode</span> (total: 16 W output)</li>
                        <li>• <span className="text-white">100 cm² active area</span> (14.6 × 6.9 cm)</li>
                        <li>• <span className="text-white">Hand-held unit</span> for precise application</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-teal-400 font-semibold mb-3">Application Protocol</h4>
                      <ul className="space-y-2 text-gray-300 text-sm">
                        <li>• Applied over <span className="text-white">cardiac apex</span></li>
                        <li>• <span className="text-white">5th intercostal space</span> at midclavicular line</li>
                        <li>• Corresponds to <span className="text-white">mitral auscultation area</span></li>
                        <li>• <span className="text-white">Contact application</span> with skin</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-teal-400 font-semibold mb-3">Energy Parameters</h4>
                      <ul className="space-y-2 text-gray-300 text-sm">
                        <li>• <span className="text-white">2 W/cm² peak irradiance</span> per diode</li>
                        <li>• <span className="text-white">30 seconds</span> treatment duration</li>
                        <li>• <span className="text-white">6 J energy</span> per laser diode</li>
                        <li>• <span className="text-white">60 J/cm² fluence</span> per diode</li>
                      </ul>
                    </div>
                    
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-teal-400 font-semibold mb-3">Total Dose Delivered</h4>
                      <ul className="space-y-2 text-gray-300 text-sm">
                        <li>• <span className="text-white">480 J total energy</span> (across all diodes)</li>
                        <li>• <span className="text-white">160 mW/cm² average</span> surface irradiance</li>
                        <li>• <span className="text-white">4.8 J/cm² average</span> surface fluence</li>
                        <li>• Optimized for <span className="text-white">cardiac cellular activation</span></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Forward Looking Statement */}
          <motion.div
            className="bg-gradient-to-r from-teal-900/20 to-cyan-800/20 backdrop-blur-sm rounded-3xl p-8 border border-teal-500/30"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-teal-400 mb-4">Forward Looking Statement</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  Our comprehensive first-in-man clinical data utilizing targeted echocardiographic 
                  measurement of E/A ratios has demonstrated the significant potential of the Synergize 
                  protocol in improving cardiac diastolic function.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  This groundbreaking data will be released as part of our forward-looking statements, 
                  marking a pivotal moment in cardiovascular optimization therapy. The results validate 
                  our innovative approach to combining substrate loading with photobiomodulation for 
                  enhanced ATP generation and cardiac performance.
                </p>
                <div className="flex items-center space-x-2 mt-6">
                  <AlertCircle className="w-5 h-5 text-teal-400" />
                  <span className="text-sm text-teal-400 font-medium">Clinical data release pending</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-900/40 via-gray-900 to-teal-900/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="space-y-8"
            {...fadeInUp}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Experience the
              <br />
              <span className="bg-gradient-to-r from-teal-400 to-teal-600 bg-clip-text text-transparent">
                Synergize Protocol
              </span>
            </h2>
            
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Join our pioneering clinical program and be part of the cardiovascular optimization revolution.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-teal-600 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
                Request Protocol Information
              </button>
              <button className="border-2 border-teal-500 text-teal-400 px-8 py-4 rounded-lg font-bold text-lg hover:bg-teal-500 hover:text-white transition-all duration-300">
                Clinical Data Preview
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SynergizeProtocol;